import { SERVICE_BASE_URLS } from "../global/constants";
import {
  IRebuildPlan,
  IRebuildResult,
  IRebuildVerify,
  ISpecAdoptResult,
  ISpecPlan,
} from "../types/api/restore.type";
import api from "./api";

const ROOT = `${SERVICE_BASE_URLS.DB_MANAGER}/db-environments`;

/** Read-only: the recorded spec against the cluster. */
const specPlan = () => api.get<ISpecPlan>(`${ROOT}/spec/plan`);

/**
 * Write the live spec into the record, for environments with no record or a stale one.
 *
 * Touches no Kubernetes object. This is the first-rollout backfill: until it has run, a
 * rebuild has nothing to rebuild from. `userId` is required because this acts across every
 * tenant, so the server records the caller rather than checking ownership.
 */
const specAdopt = (userId: string, envIds?: number[]) =>
  api.post<ISpecAdoptResult>(`${ROOT}/spec/adopt`, envIds?.length ? { env_ids: envIds } : {}, {
    headers: { "user-id": userId },
  });

/** Read-only: which environments can be rebuilt, and which cannot. */
const rebuildPlan = () => api.get<IRebuildPlan>(`${ROOT}/rebuild/plan`);

/**
 * Recreate missing databases from their recorded spec, under their original ids.
 *
 * Omitting `envIds` rebuilds the whole `missing` bucket — the DR path. The server bounds how
 * many run at once; a recovery that saturates the node pool is an outage on top of an outage.
 */
const rebuild = (userId: string, envIds?: number[]) =>
  api.post<IRebuildResult>(`${ROOT}/rebuild`, envIds?.length ? { env_ids: envIds } : {}, {
    headers: { "user-id": userId },
  });

/** Did the data come back, or did the pod merely restart? */
const verifyRebuild = (envId: number) =>
  api.get<IRebuildVerify>(`${ROOT}/${envId}/rebuild/verify`);

const RestoreService = {
  specPlan,
  specAdopt,
  rebuildPlan,
  rebuild,
  verifyRebuild,
};

export default RestoreService;
